/**
 * The document list's Uploader column: the uploader's initials as plain text
 * (no pill, so a column of them reads as quietly as the dates beside it), and
 * on hover their profile picture and full name.
 */

import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";

import { splitDisplayName, uploaderInitials } from "../../utils/uploaderName";

interface DocumentUploaderCellProps {
  createdBy?: string | number | null;
  createdByName?: string | null;
  createdByAvatarUrl?: string | null;
  createdByColor?: string | null;
  className?: string;
}

export function DocumentUploaderCell({
  createdBy,
  createdByName,
  createdByAvatarUrl,
  createdByColor,
  className,
}: DocumentUploaderCellProps) {
  const { t } = useLanguage();
  const name = createdByName?.trim() || "";
  const initials = uploaderInitials(name);
  const cell = cn(
    "flex min-w-0 items-center justify-center text-xs font-medium text-muted-foreground",
    className
  );

  if (!initials) {
    return (
      <div className={cell} title={t.documents.table.uploaderUnknown}>
        -
      </div>
    );
  }

  const profileId = avatarSeed(createdBy, name);
  const { first_name, last_name } = splitDisplayName(name);

  return (
    <div className={cell}>
      <HoverCard openDelay={250} closeDelay={100}>
        <HoverCardTrigger asChild>
          {/* Not a tab stop: a card per row would make tabbing through a long
              list a crawl. A screen reader reads the full name instead (no
              `title` beside it, which would announce the name twice). */}
          <span className="cursor-default select-none tracking-wide">
            <span aria-hidden="true" data-testid="document-uploader-initials">
              {initials}
            </span>
            <span className="sr-only">{name}</span>
          </span>
        </HoverCardTrigger>
        <HoverCardContent side="top" className="flex w-auto max-w-xs items-center gap-2.5 p-2.5">
          <UserAvatar
            user={{
              id: profileId,
              first_name,
              last_name,
              email: "",
              avatar_url: createdByAvatarUrl ?? null,
              color: createdByColor ?? null,
            }}
            size={32}
          />
          <span className="min-w-0 truncate text-sm font-medium text-foreground">{name}</span>
        </HoverCardContent>
      </HoverCard>
    </div>
  );
}

/**
 * The avatar's palette seed: the profile id when one is recorded, else a hash of
 * the name — `Number(null)` is 0, which would paint every id-less uploader alike.
 */
function avatarSeed(createdBy: string | number | null | undefined, name: string): number {
  if (createdBy !== null && createdBy !== undefined && createdBy !== "") {
    const id = Number(createdBy);
    if (Number.isFinite(id)) return id;
  }
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return Math.abs(hash);
}
